// src/Products.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import './styles/ManagementPages.css';

const METAL_OPTIONS = [
  { id: 1, label: 'Or', icon: '🥇' },
  { id: 2, label: 'Argent', icon: '🥈' },
  { id: 3, label: 'Platine', icon: '💎' },
  { id: 4, label: 'Palladium', icon: '⚡' },
];

const formatPrime = (value) => {
  if (value === null || value === undefined) return '—';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return '—';
  return `${numeric > 0 ? '+' : ''}${numeric.toFixed(2)} %`;
};

const getPrimeClass = (value) => {
  if (value === null || value === undefined) return '';
  const numeric = Number(value);
  if (numeric < 0) return 'management-metric__value--prime-negative';
  if (numeric <= 10) return 'management-metric__value--prime-normal';
  if (numeric <= 20) return 'management-metric__value--prime-high';
  return 'management-metric__value--prime-very-high';
};

const formatEuro = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(numeric);
};

const formatDateTime = (value) => {
  if (!value) return 'n/a';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const STATUS_FILTER_OPTIONS = [
  { id: 'tous', label: 'Tous' },
  { id: 'actifs', label: '✅ Actifs' },
  { id: 'masques', label: '🔒 Masqués' },
];

const Products = () => {
  const [products, setProducts] = useState([]);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMetals, setSelectedMetals] = useState([]);
  const [statusFilter, setStatusFilter] = useState('tous');
  const authToken = localStorage.getItem('token');

  const fetchProducts = useCallback(async () => {
    setError('');
    try {
      const response = await axios.get('/api/products', {
        params: { catalogue: 'complet' },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setProducts(response.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Erreur lors de la récupération des produits');
    }
  }, [authToken]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const filteredProducts = useMemo(() => {
    const lowerTerm = searchTerm.trim().toLowerCase();
    return products.filter((product) => {
      const matchesSearch =
        !lowerTerm ||
        product.nom?.toLowerCase().includes(lowerTerm) ||
        String(product.id).includes(lowerTerm);

      const matchesMetal =
        selectedMetals.length === 0 || selectedMetals.includes(Number(product.id_metal));

      const matchesStatus =
        statusFilter === 'tous' ||
        (statusFilter === 'masques' && product.masque) ||
        (statusFilter === 'actifs' && !product.masque);

      return matchesSearch && matchesMetal && matchesStatus;
    });
  }, [products, searchTerm, selectedMetals, statusFilter]);

  const stats = useMemo(() => {
    const total = products.length;
    const filtered = filteredProducts.length;
    const masques = products.filter((p) => p.masque).length;
    const spreadProducts = filteredProducts.filter((p) => !p.vente_seule);
    const averageSpread =
      spreadProducts.length > 0
        ? spreadProducts.reduce((acc, product) => {
            const buy = Number(product.prixA);
            const sell = Number(product.prixV);
            if (Number.isNaN(buy) || Number.isNaN(sell)) {
              return acc;
            }
            return acc + (sell - buy);
          }, 0) / spreadProducts.length
        : 0;

    const primesWithValue = filteredProducts.filter(
      (p) => p.prime !== null && p.prime !== undefined,
    );
    const averagePrime =
      primesWithValue.length > 0
        ? primesWithValue.reduce((acc, p) => acc + Number(p.prime), 0) / primesWithValue.length
        : null;

    const metalsCovered = new Set(products.map((product) => product.id_metal)).size;

    return {
      total,
      filtered,
      masques,
      averageSpread: Number.isFinite(averageSpread) ? averageSpread : 0,
      averagePrime: averagePrime !== null && Number.isFinite(averagePrime) ? averagePrime : null,
      metalsCovered,
    };
  }, [products, filteredProducts]);

  const toggleMetalFilter = (metalId) => {
    setSelectedMetals((prev) =>
      prev.includes(metalId) ? prev.filter((id) => id !== metalId) : [...prev, metalId],
    );
  };

  const getMetalLabel = (metalId) => METAL_OPTIONS.find((m) => m.id === Number(metalId))?.label;

  return (
    <div className="management-page">
      <div className="management-page__inner">
        <header className="management-header glass-panel">
          <div className="management-header__content">
            <h1 className="management-title">
              <span className="management-title__icon">💰</span>
              Catalogue des produits
            </h1>
            <p className="management-subtitle">
              Visualisez l’ensemble des produits négociés, leurs prix achat/vente et leurs
              caractéristiques essentielles à jour.
            </p>
            <div className="management-meta">
              <span className="management-badge">Cours en temps réel</span>
              <span className="management-badge">Filtres multi-métaux</span>
            </div>
          </div>
          <div className="management-header__actions">
            <button type="button" className="mgmt-btn mgmt-btn--outline" onClick={fetchProducts}>
              Actualiser
            </button>
          </div>
        </header>

        <section className="management-stats">
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Produits disponibles</span>
            <span className="management-stat-value">{stats.total}</span>
            <span className="management-stat-meta">Références totales</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Résultats filtrés</span>
            <span className="management-stat-value">{stats.filtered}</span>
            <span className="management-stat-meta">
              {stats.filtered === stats.total ? 'Tous les produits' : 'Filtre actif'}
            </span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Spread moyen</span>
            <span className="management-stat-value">{formatEuro(stats.averageSpread)}</span>
            <span className="management-stat-meta">Marge vente - achat</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Prime moyenne</span>
            <span className={`management-stat-value ${getPrimeClass(stats.averagePrime)}`}>
              {formatPrime(stats.averagePrime)}
            </span>
            <span className="management-stat-meta">Écart vente / intrinsèque</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Familles de métaux</span>
            <span className="management-stat-value">{stats.metalsCovered}</span>
            <span className="management-stat-meta">Couverture du catalogue</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Produits masqués</span>
            <span className="management-stat-value">{stats.masques}</span>
            <span className="management-stat-meta">Catalogue agences inactif</span>
          </div>
        </section>

        {error && (
          <div className="management-alert error">
            <span>⚠️</span>
            {error}
          </div>
        )}

        <section className="management-section glass-panel">
          <div className="management-section__head">
            <h2 className="management-section__title">Filtrer le catalogue</h2>
            <p className="management-section__subtitle">
              Affinez votre recherche par nom, identifiant ou par famille de métal. Les filtres se
              combinent et se réinitialisent à tout moment.
            </p>
          </div>
          <div className="management-grid management-grid--two">
            <div className="management-form__field">
              <label htmlFor="products-search" className="management-form__label">
                Recherche
              </label>
              <input
                id="products-search"
                className="mgmt-input"
                type="text"
                placeholder="Rechercher par nom ou ID (ex : Krugerrand, 12)"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className="management-form__field">
              <label className="management-form__label">Métaux</label>
              <div className="management-checkbox-grid">
                {METAL_OPTIONS.map((option) => (
                  <label key={option.id} className="management-checkbox">
                    <input
                      type="checkbox"
                      value={option.id}
                      checked={selectedMetals.includes(option.id)}
                      onChange={() => toggleMetalFilter(option.id)}
                    />
                    {option.icon} {option.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="management-form__field">
              <label className="management-form__label">Statut</label>
              <div className="management-status-filter">
                {STATUS_FILTER_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`mgmt-btn mgmt-btn--small ${
                      statusFilter === option.id ? 'mgmt-btn--primary' : 'mgmt-btn--outline'
                    }`}
                    onClick={() => setStatusFilter(option.id)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="management-actions">
            <button
              type="button"
              className="mgmt-btn mgmt-btn--ghost"
              onClick={() => {
                setSelectedMetals([]);
                setSearchTerm('');
                setStatusFilter('tous');
              }}
            >
              Réinitialiser les filtres
            </button>
          </div>
        </section>

        <section className="management-section glass-panel">
          <div className="management-section__head">
            <h2 className="management-section__title">Résultats</h2>
            <p className="management-section__subtitle">
              {filteredProducts.length} produit(s) correspondent à votre recherche.
            </p>
          </div>

          {filteredProducts.length === 0 ? (
            <div className="management-empty-state">
              <span className="management-empty-state__icon">🛒</span>
              <span className="management-empty-state__title">Aucun produit trouvé</span>
              <span className="management-empty-state__subtitle">
                Ajustez vos filtres ou réinitialisez la recherche pour retrouver l’ensemble du
                catalogue.
              </span>
            </div>
          ) : (
            <div className="management-cards-grid">
              {filteredProducts.map((product) => {
                const lowQualityUrl = product.image_url
                  ? `${product.image_url}?quality=60`
                  : null;
                const metalLabel = getMetalLabel(product.id_metal) || 'Métal';

                return (
                  <article
                    key={product.id}
                    className={`management-product-card glass-panel ${
                      product.masque ? 'management-product-card--masque' : ''
                    }`}
                  >
                    <div className="management-product-card__header">
                      <div className="management-product-card__title">
                        <h3>{product.nom}</h3>
                        <span>{product.symbol || '—'}</span>
                      </div>
                      <div>
                        <span className="management-tag management-tag--success">
                          {metalLabel}
                        </span>
                        {product.masque ? (
                          <span className="management-tag management-tag--masque">
                            🔒 Masqué
                          </span>
                        ) : null}
                        {product.masque && product.categorie && (
                          <span className="management-tag management-tag--categorie">
                            {product.categorie}
                          </span>
                        )}
                        {!product.masque && product.vente_seule && (
                          <span className="management-tag management-tag--warning">
                            Vente seule
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="management-product-card__image">
                      {lowQualityUrl ? (
                        <img src={lowQualityUrl} alt={product.nom} loading="lazy" />
                      ) : (
                        <span className="management-empty-state__subtitle">
                          Pas d&apos;image disponible
                        </span>
                      )}
                    </div>

                    <div className="management-product-card__metrics">
                      <div className="management-metric">
                        <span className="management-metric__label">Prix achat</span>
                        <span className="management-metric__value">
                          {product.vente_seule ? '—' : formatEuro(product.prixA)}
                        </span>
                      </div>
                      <div className="management-metric">
                        <span className="management-metric__label">Prix vente</span>
                        <span className="management-metric__value">
                          {formatEuro(product.prixV)}
                        </span>
                      </div>
                      <div className="management-metric">
                        <span className="management-metric__label">Valeur intrinsèque</span>
                        <span className="management-metric__value">
                          {formatEuro(product.intrinseque)}
                        </span>
                      </div>
                      <div className="management-metric">
                        <span className="management-metric__label">Prime</span>
                        <span className={`management-metric__value ${getPrimeClass(product.prime)}`}>
                          {formatPrime(product.prime)}
                        </span>
                      </div>
                      <div className="management-metric">
                        <span className="management-metric__label">Poids</span>
                        <span className="management-metric__value">
                          {product.poids ? `${product.poids} g` : '—'}
                        </span>
                      </div>
                      <div className="management-metric">
                        <span className="management-metric__label">Titrage</span>
                        <span className="management-metric__value">
                          {product.titreLabel || '—'}
                        </span>
                      </div>
                    </div>

                    <div className="management-product-card__footer">
                      <span>ID produit : #{product.id}</span>
                      <span>Dernière mise à jour : {formatDateTime(product.updated_at)}</span>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Products;